<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <!-- TODO -->
    <!-- Remove or shorten caching? -->
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <widget-integration-dialog
        v-if="integrationDialogVisible"
        :access-key="accessKey"
        @close="integrationDialogVisible=false"
      />
      <extra-info-editor
        v-if="extraInfoEditorVisible"
        v-model="a.extraInfo"
        :languages="enabledLanguages"
        @close="extraInfoEditorVisible = false"
        @save="a.extraInfo = $event"
      />
      <widget-text-overrides-dialog
        v-if="widgetWelcomeDialogVisible"
        v-model="a.welcomeMessage"
        :languages="enabledLanguages"
        mode="welcome"
        @close="widgetWelcomeDialogVisible=false"
        @save="a.welcomeMessage = $event"
      />
      <widget-text-overrides-dialog
        v-if="widgetTranslationsOverrideVisible"
        v-model="a.translations"
        :languages="enabledLanguages"
        mode="translations"
        @close="widgetTranslationsOverrideVisible=false"
        @save="a.translations = $event"
      />
      <v-alert
        v-if="godMode"
        class="mb-6"
        type="warning"
        dense
        prominent
      >
        👑 GM Mode Active 👑
      </v-alert>
      <validation-observer ref="observerRef">
        <div class="d-flex flex-column flex-sm-row">
          <div
            class="flex-grow-1 pt-2"
            style="width: 0"
          >
            <settings-group
              :title="$t('settings.online-integration.widget_appearance_title')"
              :subtitle="$t('settings.online-integration.widget_appearance_subtitle')"
              gutter
            >
              <v-col
                cols="12"
                class="pt-3 pb-0"
              >
                <v-select
                  v-model="widgetColor"
                  :items="widgetColorOptions"
                  :label="$t('label.color')"
                />

                <color-picker-field
                  v-if="widgetColor === 'custom'"
                  v-model="a.color"
                  :label="$t('label.color')"
                />

                <v-select
                  v-model="widgetLanguage"
                  :items="languageOptions"
                  :persistent-hint="widgetLanguage !== 'html' && widgetLanguage !== 'account'"
                  :hint="widgetLanguage !== 'html' && widgetLanguage !== 'account' ?
                    $t('label.widget_language_hint') : undefined"
                  :label="$t('label.language')"
                />

                <v-checkbox
                  v-model="a.open"
                  class="py-0"
                  :label="$t('label.widget_maximize_desktop')"
                />
              </v-col>
            </settings-group>
            <settings-group
              :title="$t('settings.online-widget.terms_title')"
              :subtitle="$t('settings.online-widget.terms_subtitle')"
              gutter
            >
              <v-col
                cols="12"
                class="py-0"
              >
                <v-checkbox
                  v-model="a.useTerms"
                  class="py-0"
                  :label="$t('label.widget_ask_terms')"
                />

                <v-checkbox
                  v-if="a.useTerms"
                  v-model="widgetCustomTerms"
                  class="py-0"
                  :label="$t('label.widget_custom_terms')"
                />

                <v-text-field
                  v-if="a.useTerms && widgetCustomTerms"
                  v-model="a.useTermsUrl"
                  :label="$t('label.widget_custom_terms_url')"
                />

                <v-divider />

                <v-checkbox
                  v-model="a.useNewsletterSubscription"
                  class="py-0"
                  :label="$t('label.widget_ask_newsletter')"
                />

                <v-checkbox
                  v-if="a.useNewsletterSubscription"
                  v-model="a.useNewsletterSubscriptionOptIn"
                  :label="$t('label.widget_newsletter_opt_in')"
                />
              </v-col>
            </settings-group>
            <settings-group
              :title="$t('settings.online-widget.action_button_title')"
              :subtitle="$t('settings.online-widget.action_button_subtitle')"
              gutter
            >
              <v-col
                cols="12"
                class="py-0"
              >
                <!-- <v-text-field
                  v-model="a.fabText"
                  class="py-0"
                  :label="$t('label.widget_floating_button_title')"
                /> -->
                <v-checkbox
                  v-model="a.showFab"
                  class="py-0"
                  :label="$t('label.widget_show_floating_button')"
                />
                <v-checkbox
                  v-model="a.showGiftCardFab"
                  class="py-0"
                  :label="$t('label.widget_show_gift_card_button')"
                />
              </v-col>
            </settings-group>
            <settings-group
              :title="$t('settings.online-widget.group_reservations_title')"
              :subtitle="$t('settings.online-widget.group_reservations_subtitle')"
              gutter
            >
              <v-col
                cols="12"
                class="py-0"
              >
                <v-checkbox
                  v-model="a.useMaxPartySizeLink"
                  class="py-0"
                  :label="$t('label.widget_more_people_link')"
                />
                <template v-if="a.useMaxPartySizeLink">
                  <v-checkbox
                    v-model="a.useRestaurantEmail"
                    class="py-0"
                    :label="$t('label.widget_restaurant_email_more_people')"
                  />
                  <v-checkbox
                    v-model="a.useRestaurantPhone"
                    class="py-0"
                    :label="$t('label.widget_restaurant_phone_more_people')"
                  />
                </template>
              </v-col>
            </settings-group>
            <settings-group
              :title="$t('settings.online-widget.advanced_preferences_title')"
              :subtitle="$t('settings.online-widget.advanced_preferences_subtitle')"
              gutter
            >
              <v-col
                cols="12"
                class="py-0"
              >
                <v-checkbox
                  v-model="a.useReservationNotes"
                  class="py-0"
                  :label="$t('label.widget_reservation_notes')"
                />
                <v-checkbox
                  v-model="a.useAllergiesToggle"
                  class="py-0"
                  :label="$t('label.widget_allergies_toggle')"
                />
                <v-select
                  v-model="a.additionalContactData"
                  :items="additionalContactOptions"
                  multiple
                  :label="$t('label.widget_additional_contact_data')"
                />
                <v-select
                  v-model="widgetPartySizeBreakdown"
                  :items="partySizeBreakdownOptions"
                  :label="$t('label.widget_age_groups')"
                />

                <v-divider class="pb-2" />
                <v-select
                  v-model="a.messageFullyBooked"
                  :items="customMessageFullyBookedOptions"
                  :label="$t('label.setting_widget_message_fully_booked')"
                />
                <v-select
                  v-model="a.messageNoAvailability"
                  :items="customMessageNotAvailableOptions"
                  :label="$t('label.setting_widget_message_no_availability')"
                />
                <v-select
                  v-model="a.messageClosed"
                  :items="customMessageClosedOptions"
                  :label="$t('label.setting_widget_message_closed')"
                />
              </v-col>
            </settings-group>
            <settings-group
              v-if="godMode"
              title="GM Mode"
              gutter
            >
              <v-col
                cols="12"
                class="py-0"
              >
                <v-textarea
                  v-model="a.translations"
                  label="Translations Override (JSON)"
                  hide-details
                />
                <v-btn
                  class="mt-2 mb-4"
                  color="success"
                  small
                  @click="widgetTranslationsOverrideVisible = true"
                >
                  Edit
                </v-btn>
                <v-btn
                  class="mt-2 mb-4"
                  color="error"
                  small
                  @click="a.translations = undefined"
                >
                  Clear
                </v-btn>
                <v-divider />
                <v-textarea
                  v-model="a.extraInfo"
                  label="Extra Info (JSON)"
                  hide-details
                />
                <v-btn
                  class="mt-2 mb-4"
                  color="success"
                  small
                  @click="extraInfoEditorVisible = true"
                >
                  Edit
                </v-btn>
                <v-btn
                  class="mt-2 mb-4"
                  color="error"
                  small
                  @click="a.extraInfo = undefined"
                >
                  Clear
                </v-btn>
                <v-divider />
                <v-textarea
                  v-model="a.welcomeMessage"
                  label="Welcome Message (JSON)"
                  hide-details
                />
                <v-btn
                  color="success"
                  class="mt-2 mb-4"
                  small
                  @click="widgetWelcomeDialogVisible = true"
                >
                  Edit
                </v-btn>
                <v-btn
                  color="error"
                  class="mt-2 mb-4"
                  small
                  @click="a.welcomeMessage = undefined"
                >
                  Clear
                </v-btn>
                <v-divider />
              </v-col>
            </settings-group>
          </div>
          <div
            style="width: 380px;"
            class="pt-0 ml-auto mr-auto pl-0 pl-sm-2 "
          >
            <div :class="['widget-host-sticky', {'compact': true}]">
              <label
                :class="`v-label v-label--active ${$vuetify.theme.dark ? 'theme--dark' : 'theme--light'} noselect pl-4`"
                style="font-size: 12px"
                @click="attemptGodMode"
              >
                {{ $t('label.preview') }}
              </label>
              <div
                id="widget-host-container"
                :class="['pl-4', {'divider': $vuetify.breakpoint.smAndUp}]"
              >
                <iframe
                  id="widget-host-frame"
                  :key="widgetScript"
                  :srcdoc="iframeContent"
                  title="preview"
                />
              </div>
            </div>
          </div>
        </div>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import {
  computed, ref, watch,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performAction } from '@/services/vue-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import grpcClient from '@/grpc-api/grpc-client';
import { ValidationObserver } from 'vee-validate';
import useUnsavedGuard from '@/composables/unsaved-guard';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import useStoreWatch from '@/composables/store-watch';
import ColorPickerField from '@/components/pickers/ColorPickerField.vue';
import WidgetAccountSettings from '@/grpc-api/model/widget-account-settings';
import WidgetIntegrationDialog from '@/components/views/settings/widget/WidgetIntegrationDialog.vue';
import ExtraInfoEditor from '@/components/views/settings/widget/ExtraInfoEditor.vue';
import WidgetTextOverridesDialog from '@/components/views/settings/widget/WidgetTextOverridesDialog.vue';
import { widgetSupportedLanguageKeys } from './settings/widget-settings';

const a = ref(new WidgetAccountSettings());
const olda = ref(new WidgetAccountSettings());

const integrationDialogVisible = ref(false);

const widgetColorOptions = computed(() => [
  { text: i18n.t('label.widget_color_account'), value: 'account' },
  { text: i18n.t('label.widget_color_custom'), value: 'custom' },
]);
const widgetColor = ref('account');
watch(widgetColor, () => {
  if (widgetColor.value === 'account') {
    a.value.color = '';
  } else {
    a.value.color = olda.value.color ?? '#3355ff';
  }
});

const widgetLanguage = ref('account');
const languageOptions = computed(() => [
  { text: i18n.t('label.widget_language_account'), value: 'account' },
  { text: i18n.t('label.widget_language_detect_html'), value: 'html' },
  { divider: true },
  ...widgetSupportedLanguageKeys.map((l) => ({ text: i18n.t(`code.locale.${l.toLowerCase()}`), value: l })),
]);
watch(widgetLanguage, () => {
  if (widgetLanguage.value === 'account') {
    a.value.locale = '';
    a.value.useHtmlLanguage = false;
  } else if (widgetLanguage.value === 'html') {
    a.value.locale = '';
    a.value.useHtmlLanguage = true;
  } else {
    a.value.locale = widgetLanguage.value;
    a.value.useHtmlLanguage = false;
  }
});

const additionalContactOptions = computed(() => [
  { text: i18n.t('label.additional_contact_company'), value: 'company' },
  { text: i18n.t('label.additional_contact_address'), value: 'address' },
  { text: i18n.t('label.additional_contact_city'), value: 'city' },
  { text: i18n.t('label.additional_contact_zip'), value: 'postalcode' },
  { text: i18n.t('label.additional_contact_country'), value: 'country' },
  { text: i18n.t('label.additional_contact_fullname'), value: 'fullname' },
]);

const widgetPartySizeBreakdown = ref('none');
const partySizeBreakdownOptions = computed(() => [
  { text: i18n.t('label.party_size_none'), value: 'none' },
  { text: i18n.t('label.party_size_by_age'), value: 'party_size' },
  { text: i18n.t('label.party_size_by_age_child_seats'), value: 'child_seats' },
]);
watch(widgetPartySizeBreakdown, (val) => {
  const isParty = val === 'party_size';
  const isChild = val === 'child_seats';

  if (olda.value?.usePartySizeBreakdown !== undefined || isParty) {
    a.value.usePartySizeBreakdown = isParty;
  }

  if (olda.value?.usePartySizeChildSeatsBreakdown !== undefined || isChild) {
    a.value.usePartySizeChildSeatsBreakdown = isChild;
  }
});

const datePlaceholder = { date: `(${i18n.t('label.date')})` };

const callPlaceholder = computed(() => {
  const p = store.state.settings.account.phone
    ? store.state.settings.account.phone : `(${i18n.t('label.phone')})`;
  return { phone: p };
});

const customMessageFullyBookedOptions = computed(() => [
  { text: i18n.t('label.widget_message_fully_booked', datePlaceholder), value: '' },
  { text: i18n.t('label.widget_message_no_online', datePlaceholder), value: 'no-online' },
  { text: i18n.t('label.widget_message_please_call', callPlaceholder.value), value: 'please-call' },
]);

const customMessageNotAvailableOptions = computed(() => [
  { text: i18n.t('label.widget_message_not_available', datePlaceholder), value: '' },
  { text: i18n.t('label.widget_message_no_online', datePlaceholder), value: 'no-online' },
  { text: i18n.t('label.widget_message_please_call', callPlaceholder.value), value: 'please-call' },
]);

const customMessageClosedOptions = computed(() => [
  { text: i18n.t('label.widget_message_closed', datePlaceholder), value: '' },
  { text: i18n.t('label.widget_message_no_online', datePlaceholder), value: 'no-online' },
  { text: i18n.t('label.widget_message_please_call', callPlaceholder.value), value: 'please-call' },
]);

const widgetCustomTerms = ref(false);

const godMode = ref(false);
const godModeCounter = ref(0);
const attemptGodMode = () => {
  godModeCounter.value += 1;
  godMode.value = godModeCounter.value > 10;
};

// Unsaved Guard
const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value));
useUnsavedGuard(isChanged);

// Widget Account Settings
const loadWidgetAccountSettings = async (): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18n.tc('error.load_data'),
    async () => {
      const was = await grpcClient.getWidgetAccountSettings();
      a.value = cloneModel(was);
      olda.value = was;
    },
  );
  return ok;
};

const updateWidgetAccountSettings = async (): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18n.tc('error.save_data'),
    async () => {
      const was = await grpcClient.setWidgetAccountSettings(a.value);
      a.value = cloneModel(was);
      olda.value = was;
    },
  );
  return ok;
};

// store watch, load data
const accessKey = ref(undefined as undefined | string);
const reset = async () => {
  a.value = new WidgetAccountSettings();
  olda.value = new WidgetAccountSettings();

  if (!store.getters.isLoaded) return;

  await loadWidgetAccountSettings();

  accessKey.value = store.state.settings.account.defaultAccessKey;

  widgetColor.value = a.value.color ? 'custom' : 'account';

  widgetLanguage.value = a.value.useHtmlLanguage ? 'html' : (a.value.locale ?? 'account');

  if (a.value.usePartySizeChildSeatsBreakdown) {
    widgetPartySizeBreakdown.value = 'child_seats';
  } else if (a.value.usePartySizeBreakdown) {
    widgetPartySizeBreakdown.value = 'party_size';
  } else {
    widgetPartySizeBreakdown.value = 'none';
  }

  if (a.value.useTerms && a.value.useTermsUrl) {
    widgetCustomTerms.value = true;
  } else {
    widgetCustomTerms.value = false;
  }
};

useStoreWatch(reset, true);

const save = async () => {
  const ok = await updateWidgetAccountSettings();
  return ok;
};

const widgetScript = computed(() => {
  if (!accessKey.value) { return ''; }

  let widgetDomain = 'https://cdn.guestplan.com';
  if (import.meta.env.VITE_ENDPOINT_APIV1?.includes('dev.guestplan.app')) {
    widgetDomain = 'https://widget.guestplan.app';
  }

  let s = `
  (function(g,s,t,p,l,n){
    g["_gstpln"]={};
    (l=s.createElement(t)),(n=s.getElementsByTagName(t)[0]);
    l.async=1;l.src=p;n.parentNode.insertBefore(l,n);
  })(window,document,"script","${widgetDomain}/widget.js");
  _gstpln.accessKey = "${accessKey.value}";
`;
  if (a.value.color && a.value.color.length > 0) {
    s += `_gstpln.color = "${a.value.color}";`;
  } else {
    s += `_gstpln.color = "${store.state.settings.account.color}";`;
  }

  if (a.value.locale && a.value.locale.length > 0 && !a.value.useHtmlLanguage) {
    s += `_gstpln.locale = "${a.value.locale}";`;
  }
  if ((a.value.locale ?? '') === '' && !a.value.useHtmlLanguage) {
    s += `_gstpln.locale = "${store.state.settings.account.locale}";`;
  }
  if (a.value.useHtmlLanguage !== undefined) {
    s += `_gstpln.useHtmlLanguage = ${a.value.useHtmlLanguage};`;
  }
  if (a.value.open !== undefined) {
    s += `_gstpln.open = ${a.value.open};`;
  }
  if (a.value.useTerms !== undefined) {
    s += `_gstpln.useTerms = ${a.value.useTerms};`;
  }
  if (a.value.useTermsUrl && a.value.useTermsUrl.length > 0) {
    s += `_gstpln.useTermsUrl = "${a.value.useTermsUrl}";`;
  }
  if (a.value.useNewsletterSubscription !== undefined) {
    s += `_gstpln.useNewsletterSubscription = ${a.value.useNewsletterSubscription};`;
  }
  if (a.value.useNewsletterSubscriptionOptIn !== undefined) {
    s += `_gstpln.useNewsletterSubscriptionOptIn = ${a.value.useNewsletterSubscriptionOptIn};`;
  }
  if (a.value.fabText && a.value.fabText.length > 0) {
    s += `_gstpln.fabText = "${a.value.fabText}";`;
  }
  if (a.value.showFab !== undefined) {
    s += `_gstpln.showFab = ${a.value.showFab};`;
  }
  if (a.value.showGiftCardFab !== undefined) {
    s += `_gstpln.showGiftCardFab = ${a.value.showGiftCardFab};`;
  }
  if (a.value.useMaxPartySizeLink !== undefined) {
    s += `_gstpln.useMaxPartySizeLink = ${a.value.useMaxPartySizeLink};`;
  }
  if (a.value.useRestaurantPhone !== undefined) {
    s += `_gstpln.useRestaurantPhone = ${a.value.useRestaurantPhone};`;
  }
  if (a.value.useRestaurantEmail !== undefined) {
    s += `_gstpln.useRestaurantEmail = ${a.value.useRestaurantEmail};`;
  }
  if (a.value.useReservationNotes !== undefined) {
    s += `_gstpln.useReservationNotes = ${a.value.useReservationNotes};`;
  }
  if (a.value.useAllergiesToggle !== undefined) {
    s += `_gstpln.useAllergiesToggle = ${a.value.useAllergiesToggle};`;
  }
  if (a.value.additionalContactData && a.value.additionalContactData.length > 0) {
    s += `_gstpln.additionalContactData = ${JSON.stringify(a.value.additionalContactData)};`;
  }
  if (a.value.usePartySizeBreakdown !== undefined) {
    s += `_gstpln.usePartySizeBreakdown = ${a.value.usePartySizeBreakdown};`;
  }
  if (a.value.usePartySizeChildSeatsBreakdown !== undefined) {
    s += `_gstpln.usePartySizeChildSeatsBreakdown = ${a.value.usePartySizeChildSeatsBreakdown};`;
  }
  if (a.value.messageFullyBooked && a.value.messageFullyBooked.length > 0) {
    s += `_gstpln.messageFullyBooked = "${a.value.messageFullyBooked}";`;
  }
  if (a.value.messageNoAvailability && a.value.messageNoAvailability.length > 0) {
    s += `_gstpln.messageNoAvailability = "${a.value.messageNoAvailability}";`;
  }
  if (a.value.messageClosed && a.value.messageClosed.length > 0) {
    s += `_gstpln.messageClosed = "${a.value.messageClosed}";`;
  }
  return s;
});

// eslint-disable-next-line no-useless-escape, vue/max-len
const iframeContent = computed(() => `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body { margin: 0; padding: 0; }</style></head><body><script>${widgetScript.value}<\/script></body></html>`);

const extraInfoEditorVisible = ref(false);
const enabledLanguages = computed(() => store.state.settings.account.enabledLocales);

const widgetWelcomeDialogVisible = ref(false);
const widgetTranslationsOverrideVisible = ref(false);

</script>

<style lang="scss" scoped>

#widget-host-container {
  width: 380px;
  height: calc(100vh - 225px);
  overflow-x: hidden;
  position: relative;
  overflow-y: scroll;
}

.widget-host-sticky {
  position: sticky;
  top: 172px;

  &.compact {
    top: 148px !important;
  }
}

#widget-host-container.divider {
  border-left: 1px solid rgba(0,0,0,0.12);
  .theme--dark & {
    border-left: 1px solid rgba(255,255,255,0.12);
  }
}

#widget-host-frame {
  position: absolute;
  width:600px;
  height: 100%;
  left: -210px;
  border-left: 1px solid gray;
  border-top: 0;
  border-bottom: 0;
  border-right: 0;

}

</style>
